import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import CloseIcon from '@mui/icons-material/Close';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  IconButton,
  Button,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';

function OutagesDialog({ open, onClose, outages, onSave, cell }) {
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState({});

  const isStock = cell?.attributes?.name === 'Stock';

  const TYPE_OPTIONS_STOCK = [
    { value: 'Lines', label: 'Lignes' },
    { value: 'ExitLines', label: 'Lignes de sortie' },
    { value: 'Places', label: 'Places' },
  ];
  const TYPE_OPTIONS_DEFAULT = [{ value: 'Lines', label: 'Lignes' }];

  const typeOptions = isStock ? TYPE_OPTIONS_STOCK : TYPE_OPTIONS_DEFAULT;

  const typeLabelToValue = Object.fromEntries(typeOptions.map((o) => [o.label, o.value]));
  const typeValueToLabel = Object.fromEntries(typeOptions.map((o) => [o.value, o.label]));

  const rowHasData = (row) =>
    ['type', 'impact', 'startTime', 'endTime'].some((k) => row[k] !== '' && row[k] !== null && row[k] !== undefined);

  const isEmptyValue = (val, kind) => {
    if (kind === 'number') return val === '' || val === null || val === undefined || Number.isNaN(Number(val));
    return val === '' || val === null || val === undefined;
  };

  useEffect(() => {
    if (open) {
      const initial = Array.isArray(outages)
        ? outages.map((o) => {
            let normalizedType = o.type;
            if (normalizedType in typeLabelToValue) {
              normalizedType = typeLabelToValue[normalizedType];
            }
            return {
              id: o.id || uuidv4(),
              type: normalizedType || '',
              impact: o.impact ?? '',
              startTime: o.startTime ?? '',
              endTime: o.endTime ?? '',
            };
          })
        : [];
      setRows(initial);
      setDirty(false);
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, outages, isStock]);

  const handleAdd = () => {
    setRows((prev) => [...prev, { id: uuidv4(), type: '', impact: '', startTime: '', endTime: '' }]);
    setDirty(true);
  };

  const handleRemove = (id) => {
    const newRows = rows.filter((r) => r.id !== id);
    setRows(newRows);
    setDirty(true);
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (k.startsWith(`${id}.`)) delete next[k];
      });
      return next;
    });
  };

  const handleChange = (id, key, value) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)));
    setDirty(true);
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`${id}.${key}`];
      return next;
    });
  };

  const validateActiveRows = () => {
    const newErrors = {};
    rows.forEach((row) => {
      if (!rowHasData(row)) return;
      if (isEmptyValue(row.type, 'select')) newErrors[`${row.id}.type`] = true;
      if (isEmptyValue(row.impact, 'number')) newErrors[`${row.id}.impact`] = true;
      if (isEmptyValue(row.startTime, 'time')) newErrors[`${row.id}.startTime`] = true;
      if (isEmptyValue(row.endTime, 'time')) newErrors[`${row.id}.endTime`] = true;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = (event, reason) => {
    if (!validateActiveRows()) {
      window.alert('Veuillez compléter tous les champs requis dans chaque ligne active avant de quitter.');
      return;
    }
    if (dirty) {
      onSave(rows);
    }
    onClose(event, reason);
  };

  if (!open) return null;

  const cellHasError = (id, key) => !!errors[`${id}.${key}`];

  return (
    <Dialog open onClose={handleClose} maxWidth="md" fullWidth disablePortal sx={{ zIndex: 10000 }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Edition pannes
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Impact</TableCell>
              <TableCell>Début</TableCell>
              <TableCell>Fin</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <FormControl variant="standard" fullWidth required error={cellHasError(row.id, 'type')}>
                    <Select
                      value={row.type ?? ''}
                      onChange={(e) => handleChange(row.id, 'type', e.target.value)}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) return <span style={{ opacity: 0.6 }}>Sélectionnez…</span>;
                        return typeValueToLabel[selected] ?? selected;
                      }}
                      MenuProps={{ disablePortal: true }}
                    >
                      <MenuItem value="" disabled>
                        <em>Sélectionnez…</em>
                      </MenuItem>
                      {typeOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {cellHasError(row.id, 'type') && <FormHelperText>Requis</FormHelperText>}
                  </FormControl>
                </TableCell>

                <TableCell>
                  <TextField
                    type="number"
                    variant="standard"
                    fullWidth
                    required
                    error={cellHasError(row.id, 'impact')}
                    helperText={cellHasError(row.id, 'impact') ? 'Requis' : undefined}
                    value={row.impact}
                    onChange={(e) => handleChange(row.id, 'impact', e.target.value)}
                    onWheel={(e) => e.target.blur()}
                    InputProps={{
                      inputProps: { min: 0 },
                      sx: {
                        // eslint-disable-next-line max-len
                        '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button':
                          {
                            WebkitAppearance: 'none',
                            margin: 0,
                          },
                        '& input[type=number]': {
                          MozAppearance: 'textfield',
                        },
                      },
                    }}
                  />
                </TableCell>

                <TableCell>
                  <TextField
                    type="time"
                    variant="standard"
                    fullWidth
                    inputProps={{ step: 1 }}
                    required
                    error={cellHasError(row.id, 'startTime')}
                    helperText={cellHasError(row.id, 'startTime') ? 'Requis' : undefined}
                    value={row.startTime}
                    onChange={(e) => handleChange(row.id, 'startTime', e.target.value)}
                  />
                </TableCell>

                <TableCell>
                  <TextField
                    type="time"
                    variant="standard"
                    fullWidth
                    inputProps={{ step: 1 }}
                    required
                    error={cellHasError(row.id, 'endTime')}
                    helperText={cellHasError(row.id, 'endTime') ? 'Requis' : undefined}
                    value={row.endTime}
                    onChange={(e) => handleChange(row.id, 'endTime', e.target.value)}
                  />
                </TableCell>

                <TableCell>
                  <IconButton size="small" onClick={() => handleRemove(row.id)}>
                    <HighlightOffIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
          <Button variant="outlined" onClick={handleAdd}>
            Ajouter une panne
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

OutagesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  outages: PropTypes.array,
  onSave: PropTypes.func.isRequired,
  cell: PropTypes.object.isRequired,
};

export default OutagesDialog;
